import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { TextFieldStatuses } from '@picsart/design-system/components/TextField';
import { allTemplates } from 'constants/templates';
import useLandingPropsStore from 'stores/landingPropsStore';
import { LandingCategory } from 'constants/enums';

function useSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const redirectRoute = pathname.split('/')[1];
  const category = useLandingPropsStore(x => x.category);

  const getQuery = useCallback(() => {
    return searchParams.get('q')?.replace(/-/g, ' ') || '';
  }, [searchParams]);

  const [status, setStatus] = useState<TextFieldStatuses.Error | undefined>(undefined);
  const [query, setQuery] = useState<string>(getQuery());

  const searchIconRef = useRef(null);

  const handleSubmit = (e: React.FormEvent, filterType?: string) => {
    e?.preventDefault();
    if (query && (!filterType || filterType === allTemplates)) {
      const urlQuery = query.split(' ').join('-');
      navigate(
        category === LandingCategory.fonts
          ? `/${redirectRoute}?q=${urlQuery}`
          : `/${redirectRoute}/search?q=${urlQuery}`,
      );
    } else if (query && filterType !== allTemplates) {
      const urlQuery = query.split(' ').join('-');
      navigate(`/${redirectRoute}/${filterType}?q=${urlQuery}`);
    } else {
      setStatus(TextFieldStatuses.Error);
    }
  };

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= 100) {
      setQuery(e.target.value);
    }
  }, []);

  const handleClear = useCallback(() => {
    setQuery('');
  }, []);

  useEffect(() => {
    const newQuery = getQuery();
    setQuery(newQuery);
  }, [getQuery, searchParams]);

  useEffect(() => {
    const onClick = () => setStatus(undefined);

    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('click', onClick);
    };
  }, []);

  return {
    status,
    query,
    handleChange,
    handleSubmit,
    handleClear,
    searchIconRef,
  };
}

export default useSearch;
